import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ImagePlus, Minus, Plus, MessageCircle, ShoppingBag, ShieldCheck, Sparkles, Star, X } from "lucide-react";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { whatsappLink } from "@/lib/site";
import ProductCard from "@/components/ProductCard";
import Rating from "@/components/Rating";
import Reveal from "@/components/Reveal";

function ReviewsSection({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [photo, setPhoto] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);

  const load = () => {
    api
      .get(`/products/${productId}/reviews`)
      .then((d) => setReviews(d.data))
      .catch(() => setReviews([]));
  };

  useEffect(() => {
    load();
  }, [productId]);

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("rating", form.rating);
      fd.append("text", form.text);
      if (photo) fd.append("photo", photo);
      await api.post(`/products/${productId}/reviews`, fd);
      toast.success("Thank you! Your review will appear once approved.");
      setForm({ name: "", rating: 5, text: "" });
      setPhoto(null);
      if (photoRef.current) photoRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-20 border-t border-line pt-12" data-testid="product-reviews">
      <Reveal y={16}>
        <p className="overline-tag">Loved By You</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Reviews</h2>
          {reviews.length > 0 && (
            <span className="flex items-center gap-2">
              <Rating value={Number(avg)} size={14} />
              <span className="font-mono text-xs text-ink3">{avg} · {reviews.length} {reviews.length === 1 ? "review" : "reviews"}</span>
            </span>
          )}
        </div>
      </Reveal>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          {reviews.length === 0 && (
            <p className="text-sm text-ink3">Be the first to review this piece.</p>
          )}
          {reviews.map((r) => (
            <div key={r._id} className="card-surface p-5" data-testid={`review-item-${r._id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-display text-lg font-semibold">{r.name}</p>
                <Rating value={r.rating} size={11} />
              </div>
              {r.text && <p className="mt-2 text-sm text-ink2 leading-relaxed">{r.text}</p>}
              {r.photo && (
                <button
                  onClick={() => setZoom(r.photo)}
                  className="mt-3 block"
                  data-testid={`review-photo-${r._id}`}
                  aria-label="View customer photo"
                >
                  <img src={r.photo} alt={`Photo from ${r.name}`} className="h-20 w-20 border border-line object-cover transition-transform duration-300 hover:scale-105" />
                </button>
              )}
              <p className="mt-2 font-mono text-[10px] text-ink3">
                {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="card-surface h-fit p-6" data-testid="review-form">
          <p className="overline-tag">Write a Review</p>
          <input
            required
            className="field mt-4"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="review-name"
          />
          <div className="mt-4 flex items-center gap-2" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                type="button"
                key={i}
                onClick={() => setForm({ ...form, rating: i })}
                className="transition-transform duration-200 hover:scale-110"
                aria-label={`${i} star`}
                data-testid={`review-star-${i}`}
              >
                <Star size={22} className={i <= form.rating ? "text-gold fill-gold" : "text-line"} />
              </button>
            ))}
          </div>
          <textarea
            rows={3}
            className="field mt-4 resize-none"
            placeholder="How do you like the piece?"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            data-testid="review-text"
          />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="btn-ghost-gold cursor-pointer !px-4 !py-2 !text-[10px]">
              <ImagePlus size={13} />
              {photo ? "Change photo" : "Add a photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setPhoto(e.target.files[0] || null)}
                ref={photoRef}
                data-testid="review-photo-input"
              />
            </label>
            {photo && (
              <>
                <img src={URL.createObjectURL(photo)} alt="Review attachment preview" className="h-12 w-12 border border-line object-cover" data-testid="review-photo-preview" />
                <button
                  type="button"
                  onClick={() => { setPhoto(null); if (photoRef.current) photoRef.current.value = ""; }}
                  className="flex items-center gap-1 font-mono text-[10px] text-ink3 hover:text-red-600"
                  data-testid="review-photo-remove"
                >
                  <X size={11} /> Remove
                </button>
              </>
            )}
          </div>
          <button type="submit" className="btn-gold mt-4 w-full justify-center" disabled={busy} data-testid="review-submit">
            {busy ? "Posting…" : "Post Review"}
          </button>
        </form>
      </div>

      {zoom && (
        <div
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-[70] grid place-items-center bg-[#14100A]/80 p-6 backdrop-blur-sm"
          data-testid="review-photo-zoom"
        >
          <img src={zoom} alt="Customer photo enlarged" className="max-h-[85vh] max-w-full border border-gold/40 object-contain shadow-2xl" />
          <button className="absolute right-5 top-5 grid h-10 w-10 place-items-center border border-gold/50 text-[#F7F4EE]" aria-label="Close photo">
            <X size={18} />
          </button>
        </div>
      )}
    </section>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart, setCartOpen, openEnquiry } = useShop();
  const [product, setProduct] = useState(null);
  const [state, setState] = useState("loading");
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setState("loading");
    setProduct(null);
    setImgIdx(0);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((d) => {
        setProduct(d.data);
        setState("ok");
        api
          .get("/products", { params: { category: d.data.category } })
          .then((r) => setRelated(r.data.filter((p) => p._id !== id).slice(0, 4)));
      })
      .catch(() => setState("error"));
  }, [id]);

  if (state === "loading") {
    return (
      <main className="max-w-[1440px] px-4 pt-32 pb-24 sm:px-8 lg:px-16 mx-auto">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="aspect-[4/5] bg-bg2 animate-pulse" />
          <div className="space-y-4"><div className="h-8 w-2/3 bg-bg2 animate-pulse" /><div className="h-6 w-1/3 bg-bg2 animate-pulse" /><div className="h-40 bg-bg2 animate-pulse" /></div>
        </div>
      </main>
    );
  }

  if (state === "error" || !product) {
    return (
      <main className="px-6 pt-40 pb-32 text-center" data-testid="product-not-found">
        <p className="font-display text-4xl text-ink">This piece has found its home</p>
        <p className="mt-3 text-sm text-ink3">It may have been sold or moved to the archive.</p>
        <Link to="/shop" className="btn-gold mt-8">Back to the Atelier</Link>
      </main>
    );
  }

  const soldOut = product.stock_status === "sold_out";
  const waMsg = `Hello Sashti Jewels, I'm interested in *${product.name}* (${inr(product.price)}). Could you share more details?`;

  return (
    <main className="pt-24 lg:pt-32">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-8 lg:py-12" data-testid="product-detail-page">
        <Link to="/shop" className="inline-flex items-center gap-1.5 font-cinzel text-[10px] uppercase text-ink3 hover:text-gold transition-colors" style={{ letterSpacing: "0.22em" }} data-testid="product-back-link">
          <ChevronLeft size={13} /> Back to the Atelier
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <div
              className="relative overflow-hidden bg-bg2 border border-line aspect-[4/5]"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoom({
                  x: ((e.clientX - r.left) / r.width) * 100,
                  y: ((e.clientY - r.top) / r.height) * 100,
                });
              }}
              onMouseLeave={() => setZoom(null)}
              data-testid="product-gallery-main"
            >
              <img
                src={product.images[imgIdx]}
                alt={product.name}
                className="h-full w-full object-contain transition-transform duration-300"
                style={
                  zoom
                    ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` }
                    : { transform: "scale(1)" }
                }
              />
              <span className="absolute right-4 top-4 chip bg-surface/90 backdrop-blur-sm">{product.finish}</span>
              {product.stock_status === "made_to_order" && (
                <span className="absolute left-4 top-4 chip bg-surface/90 backdrop-blur-sm">Made to Order</span>
              )}
              {soldOut && <span className="absolute left-4 top-4 chip bg-surface/90 backdrop-blur-sm">Sold Out</span>}
            </div>
            {product.images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {product.images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`h-20 w-16 overflow-hidden border transition-colors ${i === imgIdx ? "border-gold" : "border-line hover:border-gold/50"}`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={im} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Reveal y={16}>
              <p className="overline-tag">{product.category}</p>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold leading-tight text-ink" data-testid="product-name">{product.name}</h1>
              {product.avg_rating ? (
                <div className="mt-3 flex items-center gap-2" data-testid="product-rating-summary">
                  <Rating value={product.avg_rating} size={14} />
                  <span className="font-mono text-xs text-ink3">{product.avg_rating} · {product.review_count} {product.review_count === 1 ? "review" : "reviews"}</span>
                </div>
              ) : null}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="font-mono text-2xl text-ink" data-testid="product-detail-price">{inr(product.price)}</span>
                {product.mrp && product.mrp > product.price && (
                  <>
                    <span className="font-mono text-base text-ink3 line-through">{inr(product.mrp)}</span>
                    <span className="chip !text-[10px]" data-testid="product-savings">
                      Save {inr(product.mrp - product.price)} ({Math.round((1 - product.price / product.mrp) * 100)}% off)
                    </span>
                  </>
                )}
              </div>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <span className="chip"><ShieldCheck size={12} /> {product.finish}</span>
                {product.gross_weight_g > 0 && <span className="chip">{product.gross_weight_g} g</span>}
                <span className="chip"><Sparkles size={12} /> One of One</span>
              </div>
              <div className="gold-hr my-7" />
              <p className="text-[15px] leading-relaxed text-ink2" data-testid="product-description">{product.description}</p>
              <p className="mt-4 text-xs italic font-display text-ink3">
                Handcrafted — slight variations make each piece truly one of one.
              </p>

              <div className="mt-8 flex items-center gap-4">
                <div className="flex items-center border border-line">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-12 w-11 grid place-items-center text-ink2 hover:text-gold" data-testid="product-qty-minus"><Minus size={13} /></button>
                  <span className="w-10 text-center font-mono" data-testid="product-qty">{qty}</span>
                  <button onClick={() => setQty(Math.min(10, qty + 1))} className="h-12 w-11 grid place-items-center text-ink2 hover:text-gold" data-testid="product-qty-plus"><Plus size={13} /></button>
                </div>
                <button
                  onClick={() => { addToCart(product, qty); setCartOpen(true); }}
                  disabled={soldOut}
                  className="btn-gold flex-1 justify-center"
                  data-testid="product-add-to-cart"
                >
                  <ShoppingBag size={15} /> {soldOut ? "Sold Out" : "Add to Cart"}
                </button>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <a href={whatsappLink(waMsg)} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold justify-center" data-testid="product-whatsapp-button">
                  <MessageCircle size={15} /> WhatsApp Us
                </a>
                <button onClick={() => openEnquiry(product)} className="btn-ghost-gold justify-center" data-testid="product-enquire-button">
                  Enquire About This Piece
                </button>
              </div>
            </Reveal>
          </div>
        </div>

        <ReviewsSection productId={product._id} />

        {related.length > 0 && (
          <section className="mt-24" data-testid="related-products">
            <Reveal>
              <p className="overline-tag">Continue the Story</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold">More from {product.category}</h2>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p, i) => <ProductCard key={p._id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
